import React, { useState, useRef } from 'react';
import { X, Upload, File, Image, ChevronDown, FileText, Palette } from 'lucide-react';

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (files: File[]) => void;
}

interface FileCategory {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  supportedTypes: string[];
}

const fileCategories: FileCategory[] = [
  {
    id: 'logos',
    name: 'Logos',
    description: 'Brand logos and variations',
    icon: <Image size={20} />,
    color: 'bg-blue-500',
    supportedTypes: ['.png', '.jpg', '.jpeg', '.svg', '.ai', '.eps']
  },
  {
    id: 'images',
    name: 'Images',
    description: 'Photos and graphics',
    icon: <Image size={20} />,
    color: 'bg-purple-500',
    supportedTypes: ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp']
  },
  {
    id: 'icons',
    name: 'Icons',
    description: 'Icon sets and symbols',
    icon: <Palette size={20} />,
    color: 'bg-orange-500',
    supportedTypes: ['.png', '.svg', '.ico', '.ai', '.eps']
  },
  {
    id: 'documents',
    name: 'Documents',
    description: 'PDFs, guides, and docs',
    icon: <FileText size={20} />,
    color: 'bg-red-500',
    supportedTypes: ['.pdf', '.doc', '.docx', '.txt', '.rtf']
  }
];

export const UploadModal: React.FC<UploadModalProps> = ({
  isOpen,
  onClose,
  onUpload,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<FileCategory>(fileCategories[0]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    setSelectedFiles(files);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setSelectedFiles(files);
    }
  };

  const handleUpload = () => {
    if (selectedFiles.length > 0) {
      onUpload(selectedFiles);
      setSelectedFiles([]);
      onClose();
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col border border-border">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">Upload Files</h2>
          <button
            onClick={onClose}
            className="p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors duration-200"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="mb-6">
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              Select File Type
            </label>
            <div className="relative">
              <button
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                className="w-full flex items-center justify-between p-3 bg-muted border border-border text-foreground hover:bg-accent rounded-lg transition-colors duration-200"
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 ${selectedCategory.color} rounded-lg text-white`}>
                    {selectedCategory.icon}
                  </div>
                  <span className="font-medium">{selectedCategory.name}</span>
                </div>
                <ChevronDown size={20} className={`transition-transform duration-200 ${showCategoryDropdown ? 'rotate-180' : ''}`} />
              </button>

              {showCategoryDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-lg z-10 overflow-hidden">
                  {fileCategories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => {
                        setSelectedCategory(category);
                        setShowCategoryDropdown(false);
                      }}
                      className="w-full flex items-center gap-3 p-3 text-foreground hover:bg-accent transition-colors duration-200"
                    >
                      <div className={`p-2 ${category.color} rounded-lg text-white`}>
                        {category.icon}
                      </div>
                      <div className="text-left">
                        <div className="font-medium">{category.name}</div>
                        <div className="text-sm text-muted-foreground">{category.description}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-3 p-3 bg-muted border border-border rounded-lg">
              <div className="text-sm text-foreground mb-1 font-medium">
                Supported file types for {selectedCategory.name}:
              </div>
              <div className="text-sm text-muted-foreground">
                {selectedCategory.supportedTypes.join(', ')}
              </div>
            </div>
          </div>

          <div
            className={`border-2 border-dashed rounded-lg p-12 text-center transition-all duration-200 ${
              dragActive
                ? 'border-workspace-accent bg-workspace-accent/10'
                : 'border-border hover:border-border/80 bg-muted/30'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Upload size={48} className={`mx-auto mb-4 ${dragActive ? 'text-workspace-accent' : 'text-muted-foreground'}`} />
            <h3 className="text-lg font-medium text-foreground mb-2">
              {dragActive ? 'Drop files here' : 'Drag and drop files here'}
            </h3>
            <p className="text-muted-foreground mb-4">or</p>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-6 py-2 bg-workspace-accent hover:bg-workspace-accent-hover text-white rounded-lg transition-colors duration-200"
            >
              Choose Files
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={selectedCategory.supportedTypes.join(',')}
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {selectedFiles.length > 0 && (
            <div className="mt-6">
              <h4 className="font-medium text-foreground mb-3">Selected Files ({selectedFiles.length})</h4>
              <div className="max-h-32 overflow-y-auto space-y-2">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                    {file.type.startsWith('image/') ? (
                      <Image size={20} className="text-green-500" />
                    ) : (
                      <File size={20} className="text-muted-foreground" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{file.name}</p>
                      <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-muted/50 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={selectedFiles.length === 0}
            className="px-6 py-2 bg-workspace-accent hover:bg-workspace-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200"
          >
            Upload {selectedFiles.length > 0 && `(${selectedFiles.length})`}
          </button>
        </div>
      </div>
    </div>
  );
};

